import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  default as api,
  type CreateGoalPayload,
  type GoalWithStatus,
} from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ManagerGoalsState {
  goals: GoalWithStatus[];
  filteredGoals: GoalWithStatus[];
  selectedUserId: string | null;
  isLoading: boolean;
  error: string | null;
}

interface ManagerGoalsActions {
  fetchGoals: () => Promise<void>;
  createGoal: (payload: CreateGoalPayload) => Promise<GoalWithStatus>;
  updateGoal: (goalId: string, payload: CreateGoalPayload) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  reorderGoals: (reorderedGoals: GoalWithStatus[]) => Promise<void>;
  setSelectedUser: (userId: string | null) => void;
  clearGoals: () => void;
}

type ManagerGoalsStore = ManagerGoalsState & ManagerGoalsActions;

type ManagerGoalsStorePersist = Pick<
  ManagerGoalsStore,
  'goals' | 'filteredGoals' | 'selectedUserId'
>;

const useManagerGoalsStore = create<ManagerGoalsStore>()(
  persist<ManagerGoalsStore, [], [], ManagerGoalsStorePersist>(
    (set, get) => ({
      goals: [],
      filteredGoals: [],
      selectedUserId: null,
      isLoading: false,
      error: null,

      fetchGoals: async () => {
        const hasCachedGoals = get().goals.length > 0;
        set({ isLoading: !hasCachedGoals, error: null });

        try {
          const goals = await api.goals.getAll();
          set({
            goals,
            filteredGoals: goals,
            isLoading: false,
          });

          // Apply filter if one is selected
          const { selectedUserId } = get();
          if (selectedUserId) {
            get().setSelectedUser(selectedUserId);
          }
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : 'Failed to fetch goals',
            isLoading: false,
          });
        }
      },

      createGoal: async (payload: CreateGoalPayload) => {
        try {
          const newGoal = await api.goals.create(payload);
          set(state => {
            const updatedGoals = [...state.goals, newGoal];
            const filteredGoals = state.selectedUserId
              ? updatedGoals.filter(g =>
                  g.goal.assignedTo.some(
                    user => user._id === state.selectedUserId,
                  ),
                )
              : updatedGoals;

            return {
              goals: updatedGoals,
              filteredGoals,
            };
          });
          return newGoal;
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to create goal',
          );
        }
      },

      updateGoal: async (goalId: string, payload: CreateGoalPayload) => {
        try {
          const updatedGoal = await api.goals.update(goalId, payload);
          set(state => {
            const updatedGoals = state.goals.map(g =>
              g.goal._id === goalId ? updatedGoal : g,
            );
            const filteredGoals = state.selectedUserId
              ? updatedGoals.filter(g =>
                  g.goal.assignedTo.some(
                    user => user._id === state.selectedUserId,
                  ),
                )
              : updatedGoals;

            return {
              goals: updatedGoals,
              filteredGoals,
            };
          });
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to update goal',
          );
        }
      },

      deleteGoal: async (goalId: string) => {
        try {
          await api.goals.delete(goalId);
          set(state => {
            const updatedGoals = state.goals.filter(g => g.goal._id !== goalId);
            const filteredGoals = state.selectedUserId
              ? updatedGoals.filter(g =>
                  g.goal.assignedTo.some(
                    user => user._id === state.selectedUserId,
                  ),
                )
              : updatedGoals;

            return {
              goals: updatedGoals,
              filteredGoals,
            };
          });
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to delete goal',
          );
        }
      },

      reorderGoals: async (reorderedGoals: GoalWithStatus[]) => {
        const { selectedUserId } = get();
        // Optimistically update the UI
        set({
          goals: reorderedGoals,
          filteredGoals: selectedUserId
            ? reorderedGoals.filter(g =>
                g.goal.assignedTo.some(user => user._id === selectedUserId),
              )
            : reorderedGoals,
        });

        try {
          const payload = reorderedGoals.map((g, index) => ({
            id: g.goal._id,
            position: index,
          }));
          await api.goalsReorder(payload);
        } catch (error) {
          // Revert on failure by re-fetching
          await get().fetchGoals();
          throw new Error(
            error instanceof Error ? error.message : 'Failed to reorder goals',
          );
        }
      },

      setSelectedUser: (userId: string | null) => {
        set(state => ({
          selectedUserId: userId,
          filteredGoals: userId
            ? state.goals.filter(g =>
                g.goal.assignedTo.some(user => user._id === userId),
              )
            : state.goals,
        }));
      },

      clearGoals: () =>
        set({
          goals: [],
          filteredGoals: [],
          selectedUserId: null,
          error: null,
        }),
    }),
    {
      name: 'manager-goals-store',
      storage: createPersistStorage<ManagerGoalsStorePersist>(),
      partialize: state => ({
        goals: state.goals,
        filteredGoals: state.filteredGoals,
        selectedUserId: state.selectedUserId,
      }),
    },
  ),
);

export default useManagerGoalsStore;
